import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaTruck,
  FaCalendarAlt,
  FaChartBar,
  FaSyncAlt,
} from "react-icons/fa";

import { getComandas } from "../helpers/rutaComandas";
import "../css/resumenViajesHome.css";

const texto = (v) => String(v ?? "").trim();

const idRelacion = (v) =>
  texto(v && typeof v === "object" ? v._id : v);

const formato = (n) => Number(n || 0).toLocaleString("es-AR");

const fechaLocal = (d) => [
  d.getFullYear(),
  String(d.getMonth() + 1).padStart(2, "0"),
  String(d.getDate()).padStart(2, "0"),
].join("-");

function fechaComanda(valor) {
  const coincidencia = texto(valor).match(/^(\d{4}-\d{2}-\d{2})/);
  if (!coincidencia) return "";

  const s = coincidencia[1];
  const d = new Date(`${s}T12:00:00`);

  return !Number.isNaN(d.getTime()) && fechaLocal(d) === s ? s : "";
}

function fechasViajes(comandas) {
  const viajes = new Map();

  comandas.forEach((c) => {
    const nro = texto(c.nroviaje);
    const fecha = fechaComanda(c.fecha);

    if (!nro || !fecha) return;

    const clave = JSON.stringify([idRelacion(c.codtra), nro]);
    const anterior = viajes.get(clave);

    if (!anterior || fecha < anterior) viajes.set(clave, fecha);
  });

  return [...viajes.values()];
}

function mensajeError(error) {
  if (typeof error === "string") return error;

  const mensaje = error?.message || error?.msg;

  return typeof mensaje === "string"
    ? mensaje
    : "No se pudo cargar el resumen de viajes.";
}

export default function ResumenViajesHome() {
  const [fechas, setFechas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    let activo = true;

    async function cargar() {
      setCargando(true);
      setError("");

      try {
        const respuesta = await getComandas();

        if (!respuesta?.ok) {
          throw new Error(
            mensajeError(respuesta?.error || respuesta?.msg)
          );
        }

        if (!Array.isArray(respuesta.comandas)) {
          throw new Error("El listado de viajes no tiene el formato esperado.");
        }

        if (activo) setFechas(fechasViajes(respuesta.comandas));
      } catch (e) {
        if (activo) setError(mensajeError(e));
      } finally {
        if (activo) setCargando(false);
      }
    }

    cargar();

    return () => { activo = false; };
  }, [revision]);

  const hoy = fechaLocal(new Date());

  const resumen = useMemo(() => {
    const ahora = new Date(`${hoy}T12:00:00`);
    const anio = ahora.getFullYear();
    const mes = ahora.getMonth();
    const inicioMes = new Date(anio, mes, 1, 12);
    const finMes = new Date(anio, mes + 1, 0, 12);

    const lunes = new Date(ahora);
    lunes.setDate(lunes.getDate() - (lunes.getDay() + 6) % 7);

    const desdeMes = fechaLocal(inicioMes);
    const desdeSemana = fechaLocal(lunes);

    const delMes = fechas.filter((f) => f >= desdeMes && f <= hoy);
    const deSemana = fechas.filter((f) => f >= desdeSemana && f <= hoy);

    const semanas = [];
    let cursor = 1;

    // Semanas de lunes a domingo, recortadas a los límites del mes.
    while (cursor <= finMes.getDate()) {
      const inicio = new Date(anio, mes, cursor, 12);
      const diasHastaDomingo = (7 - inicio.getDay()) % 7;
      const ultimoDia = Math.min(
        finMes.getDate(),
        cursor + diasHastaDomingo
      );

      const fin = new Date(anio, mes, ultimoDia, 12);
      const desde = fechaLocal(inicio);
      const hasta = fechaLocal(fin);

      semanas.push({
        desde,
        hasta,
        etiqueta: `${cursor}–${ultimoDia}`,
        cantidad: delMes.filter((f) => f >= desde && f <= hasta).length,
        futura: desde > hoy,
        actual: desde <= hoy && hasta >= hoy,
      });

      cursor = ultimoDia + 1;
    }

    return {
      mes: delMes.length,
      semana: deSemana.length,
      semanas,
      maximo: Math.max(1, ...semanas.map((s) => s.cantidad)),
      nombreMes: ahora.toLocaleDateString("es-AR", {
        month: "long",
        year: "numeric",
      }),
      desdeSemana: lunes.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
      }),
    };
  }, [fechas, hoy]);

  const mostrar = (n) => cargando || error ? "—" : formato(n);

  return (
    <section className="dvh-resumen" aria-label="Resumen de viajes">
      <div className="dvh-cabecera">
        <div>
          <h2>Actividad de viajes</h2>
          <span className="dvh-periodo">{resumen.nombreMes}</span>
        </div>

        <div className="dvh-acciones">
          <button
            type="button"
            className="dvh-actualizar"
            onClick={() => setRevision((r) => r + 1)}
            disabled={cargando}
            aria-label="Actualizar resumen de viajes"
            title="Actualizar resumen"
          >
            <FaSyncAlt aria-hidden="true" />
          </button>

          <Link to="/dashboardviajes">
            Ver detalle <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>

      <div className="dvh-contenido" aria-busy={cargando}>
        <div className="dvh-indicador">
          <span className="dvh-icono">
            <FaTruck aria-hidden="true" />
          </span>

          <div aria-live="polite">
            <div className="dvh-label">Viajes del mes</div>
            <div className="dvh-numero">{mostrar(resumen.mes)}</div>
            <div className="dvh-nota">Acumulado hasta hoy</div>
          </div>
        </div>

        <div className="dvh-indicador dvh-semana">
          <span className="dvh-icono">
            <FaCalendarAlt aria-hidden="true" />
          </span>

          <div aria-live="polite">
            <div className="dvh-label">Esta semana</div>
            <div className="dvh-numero">{mostrar(resumen.semana)}</div>
            <div className="dvh-nota">
              Desde el lunes {resumen.desdeSemana}
            </div>
          </div>
        </div>

        <div className="dvh-grafico">
          <div className="dvh-grafico-titulo">
            <FaChartBar aria-hidden="true" />
            Viajes por semana del mes
          </div>

          {cargando ? (
            <div className="dvh-mensaje" role="status">
              Cargando actividad…
            </div>
          ) : error ? (
            <div className="dvh-error" role="alert">{error}</div>
          ) : (
            <>
              <div
                className="dvh-barras"
                style={{
                  gridTemplateColumns:
                    `repeat(${resumen.semanas.length}, minmax(0, 1fr))`,
                }}
              >
                {resumen.semanas.map((s) => (
                  <div
                    key={s.desde}
                    className={`dvh-columna ${s.actual ? "dvh-actual" : ""}`}
                  >
                    <div className="dvh-barra-zona">
                      <span className="dvh-barra-valor">
                        {s.futura ? "—" : formato(s.cantidad)}
                      </span>

                      <span
                        className="dvh-barra"
                        style={{
                          height: `${s.cantidad / resumen.maximo * 64}px`,
                        }}
                      />
                    </div>

                    <span className="dvh-dias">{s.etiqueta}</span>
                  </div>
                ))}
              </div>

              <div className="dvh-nota dvh-grafico-nota">
                Días del mes · semana actual en azul claro
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}